import React, { useEffect, useState } from 'react';
import { FileBarChart, Loader2, Save, Send, Play, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import {
  ExecutiveReportSettings,
  ReportSettings,
  UpdatedSegmentsReportSettings,
} from '../../types';

const DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

function parseRecipients(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const e = part.trim().toLowerCase();
    if (!e.includes('@') || seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
}

type LastRunBits = {
  lastRunAt?: string;
  lastRunStatus?: string;
  lastRunError?: string;
};

function LastRunBanner({ lastRun }: { lastRun: LastRunBits }) {
  if (!lastRun.lastRunAt && !lastRun.lastRunStatus) return null;
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-gray-600">
      <span className="font-semibold">Last run:</span>{' '}
      {lastRun.lastRunAt ? new Date(lastRun.lastRunAt).toLocaleString() : '—'} · status{' '}
      <span className="font-mono">{lastRun.lastRunStatus ?? '—'}</span>
      {lastRun.lastRunError ? ` · ${lastRun.lastRunError}` : ''}
    </div>
  );
}

export default function ReportsSettings() {
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Executive
  const [execSaving, setExecSaving] = useState(false);
  const [execRunning, setExecRunning] = useState(false);
  const [execTesting, setExecTesting] = useState(false);
  const [execTestEmail, setExecTestEmail] = useState('');
  const [execEnabled, setExecEnabled] = useState(false);
  const [periodDays, setPeriodDays] = useState<30 | 60 | 90>(30);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [execHour, setExecHour] = useState(8);
  const [execTimezone, setExecTimezone] = useState('America/Chicago');
  const [execRecipientsText, setExecRecipientsText] = useState('');
  const [execLastRun, setExecLastRun] = useState<LastRunBits>({});

  // Updated Segments
  const [segSaving, setSegSaving] = useState(false);
  const [segRunning, setSegRunning] = useState(false);
  const [segTesting, setSegTesting] = useState(false);
  const [segTestEmail, setSegTestEmail] = useState('');
  const [segEnabled, setSegEnabled] = useState(false);
  const [segHour, setSegHour] = useState(8);
  const [segTimezone, setSegTimezone] = useState('America/Chicago');
  const [segRecipientsText, setSegRecipientsText] = useState('');
  const [segLastRun, setSegLastRun] = useState<LastRunBits>({});

  const applySettings = (s: ReportSettings) => {
    const e = s.executive;
    setExecEnabled(e.enabled);
    setPeriodDays(e.periodDays);
    setDayOfWeek(e.dayOfWeek);
    setExecHour(e.hour);
    setExecTimezone(e.timezone || 'America/Chicago');
    setExecRecipientsText(e.recipients.join('\n'));
    setExecLastRun({
      lastRunAt: e.lastRunAt,
      lastRunStatus: e.lastRunStatus,
      lastRunError: e.lastRunError,
    });

    const u = s.updatedSegments;
    setSegEnabled(u.enabled);
    setSegHour(u.hour);
    setSegTimezone(u.timezone || 'America/Chicago');
    setSegRecipientsText(u.recipients.join('\n'));
    setSegLastRun({
      lastRunAt: u.lastRunAt,
      lastRunStatus: u.lastRunStatus,
      lastRunError: u.lastRunError,
    });
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get<ReportSettings>('/api/reports')
      .then((r) => applySettings(r.data))
      .catch(() => setMsg({ type: 'error', text: 'Failed to load report settings.' }))
      .finally(() => setLoading(false));
  }, []);

  const buildExecPayload = (): ExecutiveReportSettings => ({
    enabled: execEnabled,
    periodDays,
    dayOfWeek,
    hour: execHour,
    timezone: execTimezone.trim() || 'America/Chicago',
    recipients: parseRecipients(execRecipientsText),
  });

  const buildSegPayload = (): UpdatedSegmentsReportSettings => ({
    enabled: segEnabled,
    hour: segHour,
    timezone: segTimezone.trim() || 'America/Chicago',
    recipients: parseRecipients(segRecipientsText),
  });

  const handleExecSave = async () => {
    setExecSaving(true);
    setMsg(null);
    try {
      const res = await axios.put<ReportSettings>('/api/reports/executive', buildExecPayload());
      applySettings(res.data);
      setMsg({ type: 'success', text: 'Executive report settings saved. Schedule updated.' });
    } catch (e: any) {
      setMsg({
        type: 'error',
        text: e?.response?.data?.error || e?.response?.data?.message || 'Failed to save.',
      });
    } finally {
      setExecSaving(false);
    }
  };

  const handleExecRunNow = async () => {
    if (!window.confirm('Generate and email the executive PDF to all configured recipients now?')) {
      return;
    }
    setExecRunning(true);
    setMsg(null);
    try {
      await axios.put('/api/reports/executive', buildExecPayload());
      const res = await axios.post<{
        status: string;
        message: string;
        settings?: ReportSettings;
      }>('/api/reports/executive/run');
      if (res.data.settings) applySettings(res.data.settings);
      setMsg({ type: 'success', text: res.data.message || 'Report sent.' });
    } catch (e: any) {
      setMsg({
        type: 'error',
        text:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to run report.',
      });
    } finally {
      setExecRunning(false);
    }
  };

  const handleExecTest = async () => {
    const email = execTestEmail.trim().toLowerCase();
    if (!email.includes('@')) {
      setMsg({ type: 'error', text: 'Enter a valid test email address.' });
      return;
    }
    setExecTesting(true);
    setMsg(null);
    try {
      await axios.put('/api/reports/executive', buildExecPayload());
      const res = await axios.post<{ status: string; message: string; settings?: ReportSettings }>(
        '/api/reports/executive/test',
        { email }
      );
      if (res.data.settings) applySettings(res.data.settings);
      setMsg({ type: 'success', text: res.data.message || `Test sent to ${email}.` });
    } catch (e: any) {
      setMsg({
        type: 'error',
        text:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Test send failed.',
      });
    } finally {
      setExecTesting(false);
    }
  };

  const handleSegSave = async () => {
    setSegSaving(true);
    setMsg(null);
    try {
      const res = await axios.put<ReportSettings>(
        '/api/reports/updated-segments',
        buildSegPayload()
      );
      applySettings(res.data);
      setMsg({ type: 'success', text: 'Updated Segments report settings saved. Schedule updated.' });
    } catch (e: any) {
      setMsg({
        type: 'error',
        text: e?.response?.data?.error || e?.response?.data?.message || 'Failed to save.',
      });
    } finally {
      setSegSaving(false);
    }
  };

  const handleSegRunNow = async () => {
    if (
      !window.confirm(
        'Generate and email the Updated Segments report to all configured recipients now?'
      )
    ) {
      return;
    }
    setSegRunning(true);
    setMsg(null);
    try {
      await axios.put('/api/reports/updated-segments', buildSegPayload());
      const res = await axios.post<{
        status: string;
        message: string;
        settings?: ReportSettings;
      }>('/api/reports/updated-segments/run');
      if (res.data.settings) applySettings(res.data.settings);
      setMsg({ type: 'success', text: res.data.message || 'Report sent.' });
    } catch (e: any) {
      setMsg({
        type: 'error',
        text:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to run report.',
      });
    } finally {
      setSegRunning(false);
    }
  };

  const handleSegTest = async () => {
    const email = segTestEmail.trim().toLowerCase();
    if (!email.includes('@')) {
      setMsg({ type: 'error', text: 'Enter a valid test email address.' });
      return;
    }
    setSegTesting(true);
    setMsg(null);
    try {
      await axios.put('/api/reports/updated-segments', buildSegPayload());
      const res = await axios.post<{ status: string; message: string; settings?: ReportSettings }>(
        '/api/reports/updated-segments/test',
        { email }
      );
      if (res.data.settings) applySettings(res.data.settings);
      setMsg({ type: 'success', text: res.data.message || `Test sent to ${email}.` });
    } catch (e: any) {
      setMsg({
        type: 'error',
        text:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Test send failed.',
      });
    } finally {
      setSegTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading reports…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileBarChart className="h-5 w-5 text-indigo-500" />
          Reports
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Schedule automated reports emailed via your SMTP settings.
        </p>
      </div>

      {msg && (
        <div
          className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
            msg.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
          }`}
        >
          {msg.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          )}
          {msg.text}
        </div>
      )}

      {/* Executive Dashboard Report */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Executive Dashboard Report</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Weekly PDF with KPIs, recent campaign performance, and the next 10 queued sends.
              Default: Mondays at 8:00 America/Chicago.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={execEnabled}
              onChange={(e) => setExecEnabled(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600"
            />
            Enabled
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value) as 30 | 60 | 90)}
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Day of week</label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hour (0–23)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={execHour}
              onChange={(e) => setExecHour(Math.min(23, Math.max(0, Number(e.target.value) || 0)))}
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
            <input
              type="text"
              value={execTimezone}
              onChange={(e) => setExecTimezone(e.target.value)}
              placeholder="America/Chicago"
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Recipients (one email per line, or comma-separated)
          </label>
          <textarea
            value={execRecipientsText}
            onChange={(e) => setExecRecipientsText(e.target.value)}
            rows={4}
            placeholder={'ceo@example.com\ncfo@example.com'}
            className="w-full text-sm rounded-md border border-gray-300 px-3 py-2 font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">
            Cron will not send while Enabled is off or the recipient list is empty.
          </p>
        </div>

        <LastRunBanner lastRun={execLastRun} />

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={handleExecSave}
            disabled={execSaving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {execSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          <button
            type="button"
            onClick={handleExecRunNow}
            disabled={execRunning}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {execRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run now
          </button>
        </div>

        <div className="border-t border-gray-100 pt-4 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[12rem]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Send test to</label>
            <input
              type="email"
              value={execTestEmail}
              onChange={(e) => setExecTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            />
          </div>
          <button
            type="button"
            onClick={handleExecTest}
            disabled={execTesting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50"
          >
            {execTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test
          </button>
        </div>
      </div>

      {/* Updated Segments Report */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Updated Segments Report</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Daily HTML email with totals and per-county breakdowns for segments created or edited
              in EmailCraft since the last successful send. Skips emailing when nothing changed.
              Default: daily at 8:00 America/Chicago.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={segEnabled}
              onChange={(e) => setSegEnabled(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600"
            />
            Enabled
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hour (0–23)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={segHour}
              onChange={(e) => setSegHour(Math.min(23, Math.max(0, Number(e.target.value) || 0)))}
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
            <input
              type="text"
              value={segTimezone}
              onChange={(e) => setSegTimezone(e.target.value)}
              placeholder="America/Chicago"
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Recipients (one email per line, or comma-separated)
          </label>
          <textarea
            value={segRecipientsText}
            onChange={(e) => setSegRecipientsText(e.target.value)}
            rows={4}
            placeholder={'ops@example.com\nteam@example.com'}
            className="w-full text-sm rounded-md border border-gray-300 px-3 py-2 font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">
            Only EmailCraft segment create/update events are included. Cron does not send while
            Enabled is off or the recipient list is empty.
          </p>
        </div>

        <LastRunBanner lastRun={segLastRun} />

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={handleSegSave}
            disabled={segSaving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {segSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          <button
            type="button"
            onClick={handleSegRunNow}
            disabled={segRunning}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {segRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run now
          </button>
        </div>

        <div className="border-t border-gray-100 pt-4 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[12rem]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Send test to</label>
            <input
              type="email"
              value={segTestEmail}
              onChange={(e) => setSegTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full text-sm rounded-md border border-gray-300 px-2 py-2"
            />
          </div>
          <button
            type="button"
            onClick={handleSegTest}
            disabled={segTesting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50"
          >
            {segTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test
          </button>
        </div>
      </div>
    </div>
  );
}
